import React, { useEffect, useState } from 'react';
import { Minus, Plus, ShoppingBag } from 'lucide-react';
import { MenuItem, MenuItemOption, CartItemOption, ModifierOption, ResolvedModifierGroup } from '../../lib/types';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Textarea } from '../ui/Input';
import { formatCurrency, cn } from '../../lib/utils';
import { useMenuItemOptions, useMenuItemModifiers } from '../../lib/hooks';
interface ProductDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  onAdd: (item: MenuItem, quantity: number, options: CartItemOption[], notes: string) => void;
  primaryColor?: string;
  theme?: 'default' | 'capricho';
}
export function ProductDetailModal({
  isOpen,
  onClose,
  item,
  onAdd,
  primaryColor,
  theme = 'default'
}: ProductDetailModalProps) {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, MenuItemOption[]>>({});
  const [selectedModifiers, setSelectedModifiers] = useState<Record<string, ModifierOption[]>>({});
  const {
    options,
    loading
  } = useMenuItemOptions(item?.id);
  const {
    modifierGroups,
    loading: modifiersLoading
  } = useMenuItemModifiers(item?.id, item ? {
    name: item.name,
    restaurant_id: item.restaurant_id
  } : undefined);
  // Reset state when modal opens with new item
  useEffect(() => {
    if (isOpen && item) {
      setQuantity(1);
      setNotes('');
      setSelectedOptions({});
      setSelectedModifiers({});
    }
  }, [isOpen, item, options]);
  if (!item) return null;
  // Group options by option_group
  const groupedOptions = options.reduce((acc, opt) => {
    if (!acc[opt.option_group]) {
      acc[opt.option_group] = [];
    }
    acc[opt.option_group].push(opt);
    return acc;
  }, {} as Record<string, MenuItemOption[]>);
  // Calculate total price (existing options + modifiers)
  const optionsTotal = Object.values(selectedOptions).flat().reduce((sum, opt) => sum + opt.price_delta, 0);
  const modifiersTotal = Object.values(selectedModifiers).flat().reduce((sum, opt) => sum + opt.price_delta, 0);
  const totalPrice = (item.price + optionsTotal + modifiersTotal) * quantity;
  // Validation — existing options
  const isValid = Object.entries(groupedOptions).every(([group, groupOptions]) => {
    const isRequired = groupOptions.some(o => o.is_required);
    if (!isRequired) return true;
    const selectedInGroup = selectedOptions[group] || [];
    return selectedInGroup.length > 0;
  });
  // Validation — modifier groups
  const modifiersValid = modifierGroups.every(({
    group
  }) => {
    if (!group.is_required) return true;
    const selected = selectedModifiers[group.id] || [];
    return selected.length >= (group.min_select || 1);
  });
  const allValid = isValid && modifiersValid;
  const handleOptionToggle = (group: string, option: MenuItemOption, maxSelect: number) => {
    setSelectedOptions(prev => {
      const currentGroupSelections = prev[group] || [];
      const isSelected = currentGroupSelections.some(o => o.id === option.id);
      if (isSelected) {
        return {
          ...prev,
          [group]: currentGroupSelections.filter(o => o.id !== option.id)
        };
      } else {
        if (maxSelect === 1) {
          return {
            ...prev,
            [group]: [option]
          };
        } else if (currentGroupSelections.length < maxSelect) {
          return {
            ...prev,
            [group]: [...currentGroupSelections, option]
          };
        }
        return prev;
      }
    });
  };
  const handleModifierToggle = (groupId: string, option: ModifierOption, maxSelect: number) => {
    setSelectedModifiers(prev => {
      const current = prev[groupId] || [];
      const isSelected = current.some(o => o.id === option.id);
      if (isSelected) {
        return {
          ...prev,
          [groupId]: current.filter(o => o.id !== option.id)
        };
      } else {
        if (maxSelect === 1) {
          return {
            ...prev,
            [groupId]: [option]
          };
        } else if (current.length < maxSelect) {
          return {
            ...prev,
            [groupId]: [...current, option]
          };
        }
        return prev;
      }
    });
  };
  const handleAdd = () => {
    if (!allValid) return;
    // Merge existing options + modifiers into CartItemOption[]
    const flatOptions: CartItemOption[] = Object.entries(selectedOptions).flatMap(([group, opts]) => opts.map(opt => ({
      option_name: opt.name,
      option_group: group,
      price_delta: opt.price_delta
    })));
    const flatModifiers: CartItemOption[] = Object.entries(selectedModifiers).flatMap(([groupId, opts]) => {
      const groupName = modifierGroups.find(mg => mg.group.id === groupId)?.group.name || groupId;
      return opts.map(opt => ({
        option_name: opt.name,
        option_group: groupName,
        price_delta: opt.price_delta
      }));
    });
    onAdd(item, quantity, [...flatOptions, ...flatModifiers], notes);
    onClose();
  };
  const isCustomBuild = item.name.toLowerCase().includes('arma');
  const isCapricho = theme === 'capricho';
  return <Modal isOpen={isOpen} onClose={onClose} className={cn('sm:max-w-2xl p-0 overflow-hidden', isCapricho ? 'bg-white' : 'bg-[#0B0B0B]')} data-id="element-671">
      <div className={cn('flex flex-col h-full max-h-[90vh]', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-672">
        {/* Header Image */}
        {item.image_url && <div className={cn('relative h-72 w-full shrink-0', isCapricho ? 'bg-[#F5E9DA]' : 'bg-[#1a1a1a]')} data-id="element-673">
            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" data-id="element-674" />
            <div className={cn('absolute inset-0 bg-gradient-to-t', isCapricho ? 'from-white via-white/40 to-transparent' : 'from-[#0B0B0B] via-[#0B0B0B]/80 to-transparent')} data-id="element-675" />
          </div>}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" data-id="element-676">
          <div className="mb-6" data-id="element-677">
            <h2 className={cn('text-2xl font-bold mb-2', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} style={isCapricho ? {
            fontFamily: "'Playfair Display', serif"
          } : undefined} data-id="element-678">
              {item.name}
            </h2>
            {isCustomBuild && <div className={cn('inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-full mb-3', isCapricho ? 'bg-[#F5E9DA] text-[#8B5E3C]' : 'bg-[#F4D35E]/15 text-[#F4D35E]')} data-id="element-679">
                ✨ Personaliza tu rollo
              </div>}
            <p className={cn('leading-relaxed', isCapricho ? 'text-[#8B7355]' : 'text-[#F8F5F0]/60')} data-id="element-680">
              {item.description}
            </p>
            <div className={cn('mt-4 font-bold text-xl', isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]')} data-id="element-681">
              {formatCurrency(item.price)}
            </div>
          </div>

          {/* Existing Options (menu_item_options) */}
          {item.is_customizable && !loading && Object.entries(groupedOptions).map(([group, groupOptions]) => {
          const isRequired = groupOptions.some(o => o.is_required);
          const maxSelect = groupOptions[0]?.max_select || 1;
          const selectedCount = (selectedOptions[group] || []).length;
          return <div key={group} className={cn('mb-8 border-t pt-6', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-682">
                  <div className="flex justify-between items-baseline mb-4" data-id="element-683">
                    <h3 className={cn('font-bold text-lg', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-684">
                      {group}
                    </h3>
                    <div className="flex gap-2" data-id="element-685">
                      {isRequired && selectedCount === 0 && <span className={cn('text-xs font-semibold px-2 py-1 rounded-md', isCapricho ? 'text-[#CE1126] bg-[#CE1126]/10' : 'text-[#E63946] bg-[#E63946]/10')} data-id="element-686">
                          Requerido
                        </span>}
                      <span className={cn('text-xs font-medium px-2 py-1 rounded-md', isCapricho ? 'text-[#8B7355] bg-[#FBF7F2]' : 'text-[#F8F5F0]/50 bg-[#141414]')} data-id="element-687">
                        {maxSelect === 1 ? 'Elige 1' : `Elige hasta ${maxSelect}`}
                      </span>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-688">
                    {groupOptions.map(option => {
                const isSelected = (selectedOptions[group] || []).some(o => o.id === option.id);
                const isDisabled = !isSelected && selectedCount >= maxSelect;
                return <label key={option.id} className={cn('flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer', isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#F5E9DA]' : 'border-[#F4D35E] bg-[#F4D35E]/10' : isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] hover:border-[#8B5E3C]/30' : 'bg-[#141414] border-white/10 hover:border-white/20', isDisabled && 'opacity-40 cursor-not-allowed')} onClick={() => {
                  if (!isDisabled) handleOptionToggle(group, option, maxSelect);
                }} data-id="element-689">
                          <div className="flex items-center gap-3" data-id="element-690">
                            <div className={cn('flex items-center justify-center border transition-colors', maxSelect === 1 ? 'w-5 h-5 rounded-full' : 'w-5 h-5 rounded', isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white' : 'border-[#F4D35E] bg-[#F4D35E] text-[#0B0B0B]' : isCapricho ? 'border-[#E8DDD0] bg-white' : 'border-white/20 bg-transparent')} data-id="element-691">
                              {isSelected && maxSelect > 1 && <div className={cn('w-2.5 h-2.5 rounded-sm', isCapricho ? 'bg-white' : 'bg-[#0B0B0B]')} data-id="element-692" />}
                              {isSelected && maxSelect === 1 && <div className={cn('w-2 h-2 rounded-full', isCapricho ? 'bg-white' : 'bg-[#0B0B0B]')} data-id="element-693" />}
                            </div>
                            <span className={cn('font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-694">
                              {option.name}
                            </span>
                          </div>

                          {option.price_delta > 0 && <span className={cn('text-sm font-medium', isCapricho ? 'text-[#C8A96A]' : 'text-[#F8F5F0]/50')} data-id="element-695">
                              +{formatCurrency(option.price_delta)}
                            </span>}
                        </label>;
              })}
                  </div>
                </div>;
        })}

          {/* ── Modifier Groups (toppings extra) ── */}
          {!modifiersLoading && modifierGroups.length > 0 && <>
              {modifierGroups.map(({
            group,
            options: modOptions
          }) => {
            const maxSelect = group.max_select || modOptions.length;
            const selectedCount = (selectedModifiers[group.id] || []).length;
            return <div key={group.id} className={cn('mb-8 border-t pt-6', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-696">
                    <div className="flex justify-between items-baseline mb-1" data-id="element-697">
                      <h3 className={cn('font-bold text-lg', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-698">
                        {group.name}
                      </h3>
                      <div className="flex gap-2" data-id="element-699">
                        {group.is_required && selectedCount < (group.min_select || 1) && <span className={cn('text-xs font-semibold px-2 py-1 rounded-md', isCapricho ? 'text-[#CE1126] bg-[#CE1126]/10' : 'text-[#E63946] bg-[#E63946]/10')} data-id="element-700">
                              Requerido
                            </span>}
                        <span className={cn('text-xs font-medium px-2 py-1 rounded-md', isCapricho ? 'text-[#8B7355] bg-[#FBF7F2]' : 'text-[#F8F5F0]/50 bg-[#141414]')} data-id="element-701">
                          {maxSelect === 1 ? 'Elige 1' : `Elige hasta ${maxSelect}`}
                        </span>
                      </div>
                    </div>
                    {group.description && <p className={cn('text-sm mb-4', isCapricho ? 'text-[#8B7355]' : 'text-[#F8F5F0]/40')} data-id="element-702">
                        {group.description}
                      </p>}
                    {!group.description && <div className="mb-4" data-id="element-703" />}

                    <div className="space-y-3" data-id="element-704">
                      {modOptions.map(option => {
                  const isSelected = (selectedModifiers[group.id] || []).some(o => o.id === option.id);
                  const isDisabled = !isSelected && selectedCount >= maxSelect;
                  return <label key={option.id} className={cn('flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer', isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#F5E9DA]' : 'border-[#F4D35E] bg-[#F4D35E]/10' : isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] hover:border-[#8B5E3C]/30' : 'bg-[#141414] border-white/10 hover:border-white/20', isDisabled && 'opacity-40 cursor-not-allowed')} onClick={() => {
                    if (!isDisabled) handleModifierToggle(group.id, option, maxSelect);
                  }} data-id="element-705">
                            <div className="flex items-center gap-3" data-id="element-706">
                              <div className={cn('flex items-center justify-center border transition-colors w-5 h-5 rounded', isSelected ? isCapricho ? 'border-[#8B5E3C] bg-[#8B5E3C] text-white' : 'border-[#F4D35E] bg-[#F4D35E] text-[#0B0B0B]' : isCapricho ? 'border-[#E8DDD0] bg-white' : 'border-white/20 bg-transparent')} data-id="element-707">
                                {isSelected && <div className={cn('w-2.5 h-2.5 rounded-sm', isCapricho ? 'bg-white' : 'bg-[#0B0B0B]')} data-id="element-708" />}
                              </div>
                              <span className={cn('font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-709">
                                {option.name}
                              </span>
                            </div>

                            {option.price_delta > 0 && <span className={cn('text-sm font-medium', isCapricho ? 'text-[#C8A96A]' : 'text-[#F4D35E]')} data-id="element-710">
                                +{formatCurrency(option.price_delta)}
                              </span>}
                            {option.price_delta === 0 && <span className={cn('text-xs font-medium', isCapricho ? 'text-[#8B7355]' : 'text-[#F8F5F0]/30')} data-id="element-711">
                                Incluido
                              </span>}
                          </label>;
                })}
                    </div>

                    {selectedCount > 0 && <div className="mt-3 text-right" data-id="element-712">
                        <span className={cn('text-sm font-medium', isCapricho ? 'text-[#8B5E3C]' : 'text-[#F4D35E]')} data-id="element-713">
                          {selectedCount} seleccionado
                          {selectedCount > 1 ? 's' : ''} · +
                          {formatCurrency((selectedModifiers[group.id] || []).reduce((s, o) => s + o.price_delta, 0))}
                        </span>
                      </div>}
                  </div>;
          })}
            </>}

          {/* Notes */}
          <div className={cn('border-t pt-6 mb-6', isCapricho ? 'border-[#E8DDD0]' : 'border-white/5')} data-id="element-714">
            <div className="space-y-1.5" data-id="element-715">
              <label className={cn('block text-sm font-medium', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-716">
                Instrucciones especiales
              </label>
              <textarea placeholder="Ej. Sin cebolla, aderezo aparte..." value={notes} onChange={e => setNotes(e.target.value)} rows={3} className={cn('w-full px-3 py-2 rounded-xl border focus:outline-none focus:ring-2 resize-none', isCapricho ? 'bg-[#FBF7F2] border-[#E8DDD0] text-[#3D2B1F] placeholder-[#8B7355]/50 focus:ring-[#8B5E3C]' : 'bg-[#141414] border-white/10 text-[#F8F5F0] placeholder-[#F8F5F0]/30 focus:ring-[#F4D35E]')} data-id="element-717" />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className={cn('p-4 sm:p-6 border-t shrink-0', isCapricho ? 'bg-white border-[#E8DDD0]' : 'bg-[#0B0B0B] border-white/10')} data-id="element-718">
          <div className="flex items-center gap-4" data-id="element-719">
            {/* Quantity Controls */}
            <div className={cn('flex items-center rounded-xl p-1 shrink-0', isCapricho ? 'bg-[#FBF7F2] border border-[#E8DDD0]' : 'bg-[#141414]')} data-id="element-720">
              <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className={cn('w-10 h-10 flex items-center justify-center rounded-lg transition-all disabled:opacity-50', isCapricho ? 'text-[#8B5E3C] hover:bg-[#F5E9DA]' : 'text-[#F8F5F0] hover:bg-[#1a1a1a]')} disabled={quantity <= 1} data-id="element-721">
                <Minus className="w-4 h-4" data-id="element-722" />
              </button>
              <span className={cn('w-10 text-center font-semibold', isCapricho ? 'text-[#3D2B1F]' : 'text-[#F8F5F0]')} data-id="element-723">
                {quantity}
              </span>
              <button onClick={() => setQuantity(quantity + 1)} className={cn('w-10 h-10 flex items-center justify-center rounded-lg transition-all', isCapricho ? 'text-[#8B5E3C] hover:bg-[#F5E9DA]' : 'text-[#F8F5F0] hover:bg-[#1a1a1a]')} data-id="element-724">
                <Plus className="w-4 h-4" data-id="element-725" />
              </button>
            </div>

            {/* Add Button */}
            <button className={cn('flex-1 h-12 rounded-xl text-lg font-bold flex items-center justify-center transition-all', allValid ? isCapricho ? 'bg-[#8B5E3C] text-white shadow-lg shadow-[#8B5E3C]/20 hover:bg-[#704A2E]' : 'bg-[#E63946] text-white shadow-lg shadow-[#E63946]/30 hover:bg-[#d42f3d]' : isCapricho ? 'bg-[#FBF7F2] text-[#8B7355]/50 border border-[#E8DDD0] cursor-not-allowed' : 'bg-[#141414] text-[#F8F5F0]/30 cursor-not-allowed')} onClick={handleAdd} disabled={!allValid} data-id="element-726">
              <ShoppingBag className="w-5 h-5 mr-2" data-id="element-727" />
              Agregar • {formatCurrency(totalPrice)}
            </button>
          </div>
        </div>
      </div>
    </Modal>;
}